import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, getAuthToken, isAdmin } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Upload, Download, Lock } from "lucide-react";
import { toast } from "sonner";

export default function DiscountSlabs() {
  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlab, setEditingSlab] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const bulkFileInputRef = useRef(null);
  const userIsAdmin = isAdmin();
  const [formData, setFormData] = useState({
    min_invoice_value: "",
    max_invoice_value: "",
    wellcore_discount: "",
    dynamite_discount: "",
    okami_discount: "",
    youwefit_discount: "",
    zero_sugar_discount: ""
  });

  useEffect(() => {
    fetchSlabs();
  }, []);

  const fetchSlabs = async () => {
    try {
      const response = await axios.get(`${API}/discount-slabs`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setSlabs(response.data);
    } catch (error) {
      toast.error("Error fetching discount slabs");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBulk(true);
    try {
      const formDataFile = new FormData();
      formDataFile.append("file", file);

      const response = await axios.post(`${API}/discount-slabs/upload`, formDataFile, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        }
      });

      toast.success(`Uploaded ${response.data.success} slabs successfully. ${response.data.failed} failed.`);
      if (response.data.errors.length > 0) {
        console.log("Upload errors:", response.data.errors);
      }
      fetchSlabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error uploading file");
      console.error("Upload error:", error);
    } finally {
      setUploadingBulk(false);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = "";
      }
    }
  };

  const downloadTemplate = () => {
    const csvContent = "min_invoice_value,max_invoice_value,wellcore_discount,dynamite_discount,okami_discount,youwefit_discount,zero_sugar_discount\n" +
                       "0,10000,5,5.5,5,6,5.5\n" +
                       "10001,50000,7,8,7,8.5,8";
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "discount_slabs_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        min_invoice_value: parseFloat(formData.min_invoice_value),
        max_invoice_value: parseFloat(formData.max_invoice_value),
        wellcore_discount: parseFloat(formData.wellcore_discount),
        dynamite_discount: parseFloat(formData.dynamite_discount),
        okami_discount: parseFloat(formData.okami_discount),
        youwefit_discount: parseFloat(formData.youwefit_discount),
        zero_sugar_discount: parseFloat(formData.zero_sugar_discount)
      };
      
      if (editingSlab) {
        await axios.put(`${API}/discount-slabs/${editingSlab.id}`, payload, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        toast.success("Discount slab updated successfully");
      } else {
        await axios.post(`${API}/discount-slabs`, payload, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        toast.success("Discount slab created successfully");
      }
      setDialogOpen(false);
      setEditingSlab(null);
      resetForm();
      fetchSlabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this discount slab?")) return;
    try {
      await axios.delete(`${API}/discount-slabs/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success("Discount slab deleted successfully");
      fetchSlabs();
    } catch (error) {
      toast.error("Error deleting discount slab");
    }
  };

  const handleEdit = (slab) => {
    setEditingSlab(slab);
    setFormData({
      min_invoice_value: slab.min_invoice_value.toString(),
      max_invoice_value: slab.max_invoice_value.toString(),
      wellcore_discount: slab.wellcore_discount.toString(),
      dynamite_discount: slab.dynamite_discount.toString(),
      okami_discount: slab.okami_discount.toString(),
      youwefit_discount: slab.youwefit_discount.toString(),
      zero_sugar_discount: slab.zero_sugar_discount.toString()
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      min_invoice_value: "",
      max_invoice_value: "",
      wellcore_discount: "",
      dynamite_discount: "",
      okami_discount: "",
      youwefit_discount: "",
      zero_sugar_discount: ""
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingSlab(null);
    resetForm();
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="discount-slabs-title">Discount Slabs</h1>
          <p className="text-muted-foreground mt-1">Configure brand-specific discount rules based on invoice value</p>
        </div>
        <div className="flex gap-2">
          {userIsAdmin ? (
            <>
              <input
                type="file"
                ref={bulkFileInputRef}
                onChange={handleBulkUpload}
                accept=".csv,.xlsx,.xls"
                className="hidden"
              />
              <Button 
                onClick={() => bulkFileInputRef.current?.click()}
                variant="outline"
                disabled={uploadingBulk}
                data-testid="upload-slabs-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingBulk ? "Uploading..." : "Upload Slabs"}
              </Button>
              <Button 
                onClick={downloadTemplate}
                variant="outline"
                data-testid="download-template-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-accent hover:bg-accent/90 text-white" 
                    data-testid="add-slab-button"
                    onClick={() => {
                      setEditingSlab(null);
                      resetForm();
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Slab
                  </Button>
                </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle data-testid="slab-dialog-title">{editingSlab ? "Edit Discount Slab" : "Add New Discount Slab"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min_invoice_value">Min Invoice Value *</Label>
                    <Input
                      id="min_invoice_value"
                      type="number"
                      step="0.01"
                      value={formData.min_invoice_value}
                      onChange={(e) => setFormData({ ...formData, min_invoice_value: e.target.value })}
                      required
                      data-testid="min-invoice-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_invoice_value">Max Invoice Value *</Label>
                    <Input
                      id="max_invoice_value"
                      type="number"
                      step="0.01"
                      value={formData.max_invoice_value}
                      onChange={(e) => setFormData({ ...formData, max_invoice_value: e.target.value })}
                      required
                      data-testid="max-invoice-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Brand-Specific Discounts (%)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="wellcore_discount">Wellcore *</Label>
                      <Input
                        id="wellcore_discount"
                        type="number"
                        step="0.01"
                        value={formData.wellcore_discount}
                        onChange={(e) => setFormData({ ...formData, wellcore_discount: e.target.value })}
                        required
                        data-testid="wellcore-discount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dynamite_discount">Dynamite *</Label>
                      <Input
                        id="dynamite_discount"
                        type="number"
                        step="0.01"
                        value={formData.dynamite_discount}
                        onChange={(e) => setFormData({ ...formData, dynamite_discount: e.target.value })}
                        required
                        data-testid="dynamite-discount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="okami_discount">Okami *</Label>
                      <Input
                        id="okami_discount"
                        type="number"
                        step="0.01"
                        value={formData.okami_discount}
                        onChange={(e) => setFormData({ ...formData, okami_discount: e.target.value })}
                        required
                        data-testid="okami-discount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="youwefit_discount">YouWeFit *</Label>
                      <Input
                        id="youwefit_discount"
                        type="number"
                        step="0.01"
                        value={formData.youwefit_discount}
                        onChange={(e) => setFormData({ ...formData, youwefit_discount: e.target.value })}
                        required
                        data-testid="youwefit-discount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zero_sugar_discount">Zero Sugar *</Label>
                      <Input
                        id="zero_sugar_discount"
                        type="number"
                        step="0.01"
                        value={formData.zero_sugar_discount}
                        onChange={(e) => setFormData({ ...formData, zero_sugar_discount: e.target.value })}
                        required
                        data-testid="zero-sugar-discount-input"
                      />
                    </div>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="submit-slab-button">
                  {editingSlab ? "Update Slab" : "Create Slab"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
            </>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Lock className="h-4 w-4" />
              <span>View only - Admin access required to modify</span>
            </div>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Discount Slabs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice Range</TableHead>
                  <TableHead>Wellcore</TableHead>
                  <TableHead>Dynamite</TableHead>
                  <TableHead>Okami</TableHead>
                  <TableHead>YouWeFit</TableHead>
                  <TableHead>Zero Sugar</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slabs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No discount slabs found. Add your first slab to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  slabs.map((slab) => (
                    <TableRow key={slab.id} data-testid={`slab-row-${slab.id}`}>
                      <TableCell className="font-mono">
                        ₹{slab.min_invoice_value.toLocaleString('en-IN')} - ₹{slab.max_invoice_value.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{slab.wellcore_discount}%</TableCell>
                      <TableCell className="font-mono text-sm">{slab.dynamite_discount}%</TableCell>
                      <TableCell className="font-mono text-sm">{slab.okami_discount}%</TableCell>
                      <TableCell className="font-mono text-sm">{slab.youwefit_discount}%</TableCell>
                      <TableCell className="font-mono text-sm">{slab.zero_sugar_discount}%</TableCell>
                      <TableCell className="text-right">
                        {userIsAdmin ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(slab)}
                              data-testid={`edit-slab-${slab.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDelete(slab.id)}
                              data-testid={`delete-slab-${slab.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
